import React, { useEffect, useState } from "react";
import { useData } from "../../providers/DataProvider";

/**
 * A slim animated progress bar that appears at the very top of the viewport
 * while a region switch is in flight. It never actually tracks real progress —
 * it uses a CSS indeterminate animation so it always looks alive without needing
 * to know how many requests are outstanding.
 */
const RegionLoadingBar: React.FC = () => {
    const { isRegionLoading, isBonusTypeLoading, isRoleLoading } = useData();
    const isLoading = isRegionLoading || isBonusTypeLoading || isRoleLoading;

    // Keep the bar mounted briefly after loading ends so the fade-out plays fully
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (isLoading) {
            setVisible(true);
        } else {
            const timer = setTimeout(() => setVisible(false), 400);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    if (!visible) return null;

    return (
        <div
            role="progressbar"
            aria-label="Loading data"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={undefined} // indeterminate
            className="fixed top-0 left-0 right-0 z-[9999] h-[3px] overflow-hidden"
        >
            <div
                className={`h-full bg-white transition-opacity duration-300 ${isLoading ? "opacity-100" : "opacity-0"}`}
                style={{ animation: "region-bar-slide 1.4s ease-in-out infinite" }}
            />

            <style>{`
        @keyframes region-bar-slide {
          0%   { transform: translateX(-100%) scaleX(0.4); }
          50%  { transform: translateX(20%)   scaleX(0.6); }
          100% { transform: translateX(120%)  scaleX(0.4); }
        }
      `}</style>
        </div>
    );
};

export default RegionLoadingBar;